import { useFiles } from 'features/media/useFiles';
import { trendThumbnailUpdated } from 'features/trends/trends.slice';
import { updateFileUploadProgress } from 'features/ui/ui.slice';
import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { SignalRContext } from 'shared/contexts/signalr';
import { useToasts } from 'shared/hooks/useToasts';
import { BrkfstFile } from 'shared/typings/file';
import { FileUploadStatus } from 'shared/typings/file/enums';

export const useTrendFileNotifications = (onFileUpload?: (file: BrkfstFile) => void, onFileError?: () => void) => {
  const { connection } = useContext(SignalRContext);
  const { setErrorToast } = useToasts();
  const dispatch = useDispatch();
  const { updateFileUpload } = useFiles();

  const onInvalidFile = ({ key, error }: { key: string; error: string }) => {
    setErrorToast({ message: error });
    if (onFileError) onFileError();
    updateFileUpload(key, FileUploadStatus.UPLOAD_FAILED, error);
  };

  const onNewTrendFile = (file: BrkfstFile & { key: string }) => {
    if (onFileUpload) onFileUpload(file);
    updateFileUpload(file.key, FileUploadStatus.UPLOADED);
  };

  const onNewThumbnail = ({ fileId, thumbnailUrl, thumbnailOffset }) => {
    dispatch(trendThumbnailUpdated({ fileId, thumbnailUrl, thumbnailOffset }));
  };

  const progressHandler = ({ key, progress }) => {
    dispatch(updateFileUploadProgress({ id: key, progress: +progress }));
  };

  useEffect(() => {
    if (connection) {
      connection.on('newTrendFile', onNewTrendFile);
      connection.on('fileError', onInvalidFile);
      connection.on('newThumbnail', onNewThumbnail);
      connection.on('fileProgress', progressHandler);
    }

    return function cleanup() {
      if (connection) {
        connection?.off('newTrendFile', onNewTrendFile);
        connection?.off('fileError', onInvalidFile);
        connection.on('newThumbnail', onNewThumbnail);
        connection?.off('fileProgress', progressHandler);
      }
    };
  }, [connection]);
};
